/**
 * A breakdown read as a shape.
 *
 * A step that waits on another belongs underneath it, indented — the order of
 * work should be visible without reading a column. Everything here is defensive
 * about the data a scheduler can actually produce: a parent in another project,
 * a parent since deleted, and a cycle, because nothing stops someone pointing
 * two steps at each other and a render that hangs on that is worse than one
 * that shows it.
 */

/** The only fields the ordering needs. Anything phase-shaped satisfies it. */
export interface Orderable {
  phaseId: string;
  dependsOnPhaseId: string | null;
  seq?: number | null;
}

export interface OrderedRow<T extends Orderable> {
  phase: T;
  /** How far to indent. 0 is a step that waits on nothing. */
  depth: number;
  /** True where a dependency loops back on itself and had to be broken. */
  cycle: boolean;
}

const bySeq = <T extends Orderable>(a: T, b: T) =>
  (a.seq ?? 0) - (b.seq ?? 0) || a.phaseId.localeCompare(b.phaseId);

export function orderByDependency<T extends Orderable>(phases: T[]): OrderedRow<T>[] {
  const present = new Set(phases.map((p) => p.phaseId));

  // A parent outside this list cannot be shown as a parent, so the child is a
  // root. It is still true that it waits — the column says so — but the shape
  // can only draw what it holds.
  const parentOf = new Map<string, string | null>();
  for (const p of phases) {
    const parent = p.dependsOnPhaseId;
    parentOf.set(p.phaseId, parent && present.has(parent) ? parent : null);
  }

  const children = new Map<string, T[]>();
  const roots: T[] = [];
  for (const p of [...phases].sort(bySeq)) {
    const parent = parentOf.get(p.phaseId) ?? null;
    if (parent === null) roots.push(p);
    else children.set(parent, [...(children.get(parent) ?? []), p]);
  }

  const out: OrderedRow<T>[] = [];
  const placed = new Set<string>();

  const walk = (p: T, depth: number, cycle = false) => {
    if (placed.has(p.phaseId)) return;
    placed.add(p.phaseId);
    out.push({ phase: p, depth, cycle });
    for (const child of children.get(p.phaseId) ?? []) walk(child, depth + 1);
  };

  for (const r of roots) walk(r, 0);

  // Anything not reached is inside a cycle: it has a parent, its parent has a
  // parent, and the chain never meets a root. Break in at the lowest seq and
  // walk from there, so every step appears exactly once and the break is
  // labeled rather than silent.
  for (const p of [...phases].sort(bySeq)) {
    if (!placed.has(p.phaseId)) walk(p, 0, true);
  }

  return out;
}

/**
 * Would pointing `phaseId` at `atPhaseId` close a loop?
 *
 * Walks up from the proposed parent looking for the step being edited. A step
 * may not depend on itself, nor on anything that already waits on it.
 */
export function wouldCycle(
  phases: Orderable[], phaseId: string, atPhaseId: string | null,
): boolean {
  if (!atPhaseId) return false;
  if (atPhaseId === phaseId) return true;

  const parentOf = new Map(phases.map((p) => [p.phaseId, p.dependsOnPhaseId]));
  const seen = new Set<string>();
  let cursor: string | null | undefined = atPhaseId;
  while (cursor) {
    if (cursor === phaseId) return true;
    if (seen.has(cursor)) return false; // an existing loop, not one this would make
    seen.add(cursor);
    cursor = parentOf.get(cursor) ?? null;
  }
  return false;
}
